import { useState } from "react";
import { getThumbnailPath } from "../shared/getThumbnailPath";

/**
 * OptimizedBackgroundImage component for responsive and optimized background images
 * 
 * @param {Object} props
 * @param {string} props.src - Original image source
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {number} props.width - Width of the image (optional)
 * @param {number} props.height - Height of the image (optional)
 * @param {string} props.alt - Alternative text for accessibility (optional)
 */
const OptimizedBackgroundImage = ({ 
  src, 
  className, 
  style = {}, 
  width, 
  height, 
  alt = "Background image" 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const originalSrc = style?.backgroundImage
    ? style.backgroundImage.replace(/^url\(['"](.+)['"]\)$/, "$1")
    : src;

  const thumbnailSrc = getThumbnailPath(originalSrc, "md");

  const handleLoad = () => {
    setLoaded(true);
  };

  const handleError = () => {
    setError(true);
    console.error(`Failed to load background image: ${thumbnailSrc}`);
  };

  // Добавляем структурированные данные для SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": originalSrc,
    "name": alt,
  };

  return (
    <div
      className={`${className} ${loaded ? "bg-loaded" : ""}`}
      style={{
        ...style,
        backgroundImage: loaded && !error ? `url(${thumbnailSrc})` : undefined,
        backgroundColor: "#f3f3f3", // Placeholder color
        backgroundSize: "cover",
        backgroundPosition: "center",
        // Добавляем аспектное соотношение, если указаны width и height
        ...(width && height ? { aspectRatio: `${width}/${height}` } : {}),
      }}
      role="img"
      aria-label={alt}
    >
      {/* Скрытые структурированные данные для SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      
      {/* Скрытое изображение для предзагрузки */}
      <img
        src={thumbnailSrc}
        alt={alt}
        style={{
          opacity: 0,
          position: "absolute",
          width: "1px",
          height: "1px",
        }}
        onLoad={handleLoad}
        onError={handleError}
        width={width || undefined}
        height={height || undefined}
        loading="lazy"
      />

      {/* Запасной вариант при ошибке загрузки */}
      {error && originalSrc && originalSrc !== thumbnailSrc && (
        <img
          src={originalSrc}
          alt={alt}
          style={{
            opacity: 0,
            position: "absolute",
            width: "1px",
            height: "1px",
          }}
          onLoad={() => {
            setLoaded(true);
            style.backgroundImage = `url(${originalSrc})`;
          }}
          width={width || undefined}
          height={height || undefined}
          loading="lazy"
        />
      )}
    </div>
  );
};

export { OptimizedBackgroundImage, getThumbnailPath };
export default OptimizedBackgroundImage;
